import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import {
  CardContent,
  Grid,
  Theme,
  Typography,
  Button,
  Tab,
  Icon,
  styled,
  alpha,
  AlertTitle,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/styles";
// Components
import {
  IAircraft,
  IContentAssociatedFlight,
  IContent,
  IDynamicQuery,
  IFullFlight,
  IValidation,
  ILightboxState,
  IUser,
  IValidationError,
  ValidationErrorSeverity,
} from "@vmosolutions/oc-interfaces";
// Redux
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { OcTabs } from "src/components/OcTabs/OcTabs";
import { getInitialValues } from "src/pages/ContentManagement/ManageContentWindow";

import { Lightbox } from "src/components/Lightbox/Lightbox";
import { isPdfFileType, renderPdfFirstPageToDataUrl } from "src/utils/pdfRaster";
// You need to import the CSS only once

import { TextField } from "src/components/FormikComponents/TextField";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";

import CircularProgress from "@mui/material/CircularProgress";
import { debounce } from "lodash";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import moment from "moment-timezone";
import { AssociatedFlightDropTarget, RenderFlightInfo, IFlightWithCrew } from "./AssociatedFlightDropTarget";
import { Form, Formik, FormikProps, FormikHelpers } from "formik";
import { formatDateString } from "src/components/DateDisplay";
import { ShowUserInfo } from "src/components/ShowUserInfo/ShowUserInfo";
import { getFormFieldDiff, isDevEnv } from "src/utils/index";
import { getFlightById } from "src/redux/actions/flights/FlightData.Actions";
import { FormikTextField, FormikCheckboxField } from "src/components/FormikComponents/FormikWrappers";
import { FlightChip } from "src/components/Flights/FlightChip";
import WarningIcon from "@mui/icons-material/Warning";
import { ValidateFunction } from "ajv";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";

interface IReviewContentWindowProps {
  id: string;
  path: string[];
  windowProps: {
    content?: Partial<IContent>;
  };
}

interface ILogsheetPayload {
  content: IContent;
  flights: Partial<IFullFlight>[];
  override?: IValidationError[];
}

export interface ICustomTab {
  customSchema: string;
  tabIndex: number;
  title: string;
}

export interface ILogsheetProps {
  id: string;
  formProps: IContentForm;
  firstFlightWithCrew: IFlightWithCrew;
  setValidateCustom: (value: any) => any; // accepts form values and returns errors object.
}

export interface IFlightRow {
  /** Label shown in the header row above all flights */
  label: string;
  /** key associated to the property of the fullflight you're looking to display (if not calculated). */
  key?: string;
  /** type is passed to switch which determined the type of assigned component used to display/manipulate value. */
  type: "number" | "epoch" | "isodate" | "string" | "calculated" | "selectfield" | "unitValue" | "boolean";
  /** override default width of 100px for flight property column */
  width?: number;
  /** These special parameters change how the DateTime component is used displaying just date or time. */
  special?: "dateOnly" | "timeOnly";
  /** This function is passed the entire IFullFlight object and will return calculated value */
  getFieldValue?: (flight: IFullFlight) => any;
  /** CSS assigned here will override anything applied to header cells. */
  headerStyles?: any;
  /** CSS assigned here will override anything applied to flight values cells. */
  valueStyles?: any;
  /** determine whether this value can be edited inside the flight row */
  disabled?: boolean;
  customField?: boolean;
  required?: boolean;
  displayHeaderOnly?: boolean;
  options?: string;
}

// IContent Form
export type IContentForm = FormikProps<IContent>;

const getCustomerLogsheetCode = (): string => {
  // Allow overriding the customer logsheet via URL only in local development.
  if (isDevEnv() && typeof window !== "undefined") {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const customerLogsheetOverride = urlParams.get("cls");
    if (customerLogsheetOverride) {
      return customerLogsheetOverride;
    }
  }

  return import.meta.env.VITE_CUSTOMER;
};

const PalAirlinesLogsheet = React.lazy(() =>
  import("./Customers/PalAirlines/pvl_logsheet").then(({ PVLLogsheet }) => ({ default: PVLLogsheet }))
);
const CargoJetLogsheet = React.lazy(() =>
  import("./Customers/Cargojet/cjt_logsheet").then(({ CJTLogsheet }) => ({ default: CJTLogsheet }))
);
const CalmAirLogsheet = React.lazy(() =>
  import("./Customers/CalmAir/cav_logsheet").then(({ CAVLogsheet }) => ({ default: CAVLogsheet }))
);
const KFCargoLogsheet = React.lazy(() =>
  import("./Customers/KFCargo/kfa_logsheet").then(({ KFALogsheet }) => ({ default: KFALogsheet }))
);
const AirNorthLogsheet = React.lazy(() =>
  import("./Customers/AirNorth/ant_logsheet").then(({ ANTLogsheet }) => ({ default: ANTLogsheet }))
);
const SUTLogsheet = React.lazy(() =>
  import("./Customers/Summit/sut_logsheet").then(({ SUTLogsheet }) => ({ default: SUTLogsheet }))
);
const CentralMountainAirLogsheet = React.lazy(() =>
  import("./Customers/CentralMountainAir/cma_logsheet").then(({ CMALogsheet }) => ({ default: CMALogsheet }))
);
const PivotAirlinesLogsheet = React.lazy(() =>
  import("./Customers/PivotAirlines/ggn_logsheet").then(({ GGNLogsheet }) => ({ default: GGNLogsheet }))
);
const WasayaLogsheet = React.lazy(() =>
  import("./Customers/Wasaya/wsg_logsheet").then(({ WSGLogsheet }) => ({ default: WSGLogsheet }))
);
const DefaultLogsheet = React.lazy(() =>
  import("./Customers/Fallback/logsheet").then(({ DefaultLogsheet }) => ({ default: DefaultLogsheet }))
);

const CustomerLogsheet = (props: ILogsheetProps): JSX.Element => {
  const customer = getCustomerLogsheetCode();
  switch (customer) {
    case "pvl":
      return <PalAirlinesLogsheet {...props} />;
    case "cjt":
      return <CargoJetLogsheet {...props} />;
    case "cav":
      return <CalmAirLogsheet {...props} />;
    case "kfa":
      return <KFCargoLogsheet {...props} />;
    case "ant":
      return <AirNorthLogsheet {...props} />;
    case "sut":
      return <SUTLogsheet {...props} />;
    case "glr":
      return <CentralMountainAirLogsheet {...props} />;
    case "ggn":
      return <PivotAirlinesLogsheet {...props} />;
    case "wsg":
      return <WasayaLogsheet {...props} />;
    default:
      return <DefaultLogsheet {...props} />;
  }
};
const CustomerFormHeight = (): number => {
  const customer = getCustomerLogsheetCode();
  switch (customer) {
    case "pvl":
    case "cjt":
    case "sut":
    case "ggn":
    case "wsg":
      return 365;
    case "cav":
    case "kfa":
    case "ant":
    default:
      return 340;
  }
};

const OCReviewContentWindow = styled(CardContent)(({ theme }) => ({
  display: "flex",
  width: "100%",
  minWidth: "1090px", // any smaller than this and styling because an issue.
  height: "100%",
  "& .content_pane": {
    height: "100%",
    "& .lightbox": {
      position: "relative",
      borderRadius: "5px 5px 0px 0px",
      height: `calc(100% - ${CustomerFormHeight()}px - 40px) !important`,
      overflow: "hidden",
      transition: theme.transitions.create(["height"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.short,
      }),
      "& .lb-container": {
        position: "relative",
        zIndex: "unset",
        "& .lb-title": {
          padding: 0,
          "& span": {
            marginLeft: 20,
            fontSize: 16,
            fontWeight: 700,
            width: "auto",
          },
        },
      },
    },

    "& .search_flight_fields": {
      "& .search_aircraft": {
        width: 190,
        "& .input_control": {
          "& label": { width: "auto" },
        },
      },
      "& .search_departure": {
        width: 200,
        "& .input_control": {
          "& label": { width: "auto" },
        },
        "& .react-datepicker-wrapper": { margin: 0 },
      },
      "& .search_flights_btn": {
        padding: "2px 8px",
        fontSize: 12,
        height: 24,
        margin: 5,
        "& .search_icon": { fontSize: "1rem", marginRight: 5 },
      },
    },
    "& .flight_row": {
      "& .flight_edit_icon": {
        borderRadius: 3,
        width: 20,
        height: 20,
        backgroundColor: theme.palette.success.main,
        margin: "4px 4px 4px 0px",
        cursor: "pointer",
        "& path": {
          fill: theme.palette.primary.contrastText,
        },
      },
    },
    "& .logsheet_fields": {
      overflow: "auto",
      "& .readonly_fields, & .custom_fields": {
        width: "calc(50% - 5px)",
        minWidth: 500,
        marginTop: 10,
      },
      "& .cell_header": {
        textAlign: "center",
        color: "black",
        fontSize: 14,
        fontWeight: 700,
        "&.block": {
          width: 92,
        },
      },
      "& .cell_value": {
        width: 92,
      },
      "& .cell_value_static": {
        textAlign: "center",
        color: "black",
        fontSize: 14,
        lineHeight: "22px",
        fontWeight: 400,
        minHeight: 20,
        width: 92,
      },
      // If datetime field is is time only mode, hide date picker input
      "& .field.timefield": {
        "& .input_control .open_calendar_btn": {
          marginLeft: "-35px",
        },
        "& .react-datepicker-wrapper": {
          "& .react-datepicker__input-container": {
            "& span": { display: "none" },
            "& input": { display: "none" },
          },
          "& .react-datepicker__input-container::after": {
            display: "none",
          },
        },
      },
      "& .unitvalue": {
        margin: 0,
        "& .custom_value": { margin: 0 },
        "& .custom_unit": { margin: 0 },
      },
      "& .field": {
        margin: 0,
        padding: 0,
        width: "100%",
        alignItems: "center",
        "&.selectfield": {
          padding: "2px",
          "& .input_control .react-select-container .react-select__control": {
            backgroundColor: "inherit",
            border: "none",
            color: "inherit",
            fontWeight: "inherit",
            "& .react-select__single-value": {
              color: "inherit",
              fontWeight: "inherit",
            },
          },
        },
        "&.selectfield.content_type": {
          width: "263px",
        },
        "& .datedisplay span": {
          fontWeight: 400,
          fontSize: 14,
        },
        "& .static_value": {
          textAlign: "center",
          padding: "2px 8px",
          color: "inherit",
          fontSize: "inherit",
          fontWeight: "inherit",
          backgroundColor: "inherit",
          border: "inherit",
          width: "100%",
        },
        "& .input_control": {
          "& label": { width: "30px", color: "black", fontWeight: 700 },
          "& .react-datepicker-wrapper": {
            margin: 0,
          },
          "& .time-input-container": {
            margin: "0px 5px",
            width: "100%",
          },
          "& .timezone_tooltip": {
            alignItems: "anchor-center",
          },
          "& div.input-base, & .react-datepicker__input-container input, & .time-input-container input": {
            // Defaults for these styles are applied in the component.
            color: "inherit",
            fontSize: "inherit",
            fontWeight: "inherit",
            backgroundColor: "inherit",
            border: "inherit",
            width: "100%",
            "& input, & textarea": {
              fontSize: "inherit",
              fontWeight: "inherit",
            },
          },
        },
        "&.disabled .input_control": {
          "& .react-datepicker-wrapper": { opacity: 1 },
          "& div.input-base, & .react-datepicker__input-container input, & .time-input-container input": {
            opacity: 1,
            border: "none",
          },
        },
        "& .error-message": {
          margin: 0,
        },
        "&.error": {
          "& .input_control div.input-base, & .input_control .react-datepicker__input-container input, & .input_control .time-input-container input, &.selectfield .input_control .react-select-container .react-select__control":
            {
              color: theme.custom.forms.errorColor,
              border: "none", // this is handle internally by the form fields
            },
          "& .static_value": {
            color: "#00CC00",
          },
        },
        // Show fields as dirty
        "&.dirty": {
          "& .input_control div.input-base, & .input_control .react-datepicker__input-container input, & .input_control .time-input-container input, &.selectfield .input_control .react-select-container .react-select__control":
            {
              color: theme.custom.forms.alternateDirtyColor,
              borderBottom: theme.custom.forms.alternateDirtyColor,
              border: "1px solid " + `${theme.custom.forms.alternateDirtyColor} !important`,
            },
          "& .static_value": {
            color: theme.custom.forms.alternateDirtyColor,
          },
        },
      },
    },
    "& .form_actions": {
      width: "auto",
      "& .MuiButtonBase-root": {
        padding: "12px 7px",
        "&.reset,&.save,&.refresh,&.review": { padding: "2px 8px", margin: "0px 5px", fontSize: 12 },
      },
    },
    "& .flight_fields, & .final_review": {
      width: "100%",
      minWidth: 500,
      marginTop: 10,
      overflow: "auto",
      flexWrap: "nowrap",
      "& .pending_flight_updates": {
        width: "calc(100% - 10px)",
        padding: "10px",
        border: `1px solid ${alpha(theme.palette.primary.contrastText, 0.2)}`,
        marginBottom: "10px",
      },
      "& .validation_container, & .validation_history_container": {
        minHeight: "210px",
        padding: "10px",
        border: `1px solid ${alpha(theme.palette.primary.contrastText, 0.2)}`,
      },
      "& .isvalid_boolean, .haserror_boolean": {
        width: "auto",
      },
      "& .isvalid_boolean.checked": {
        "& .input_control label": {
          "& .label": { color: theme.palette.success.light },
          "& span svg": { color: theme.palette.success.light },
        },
      },
      "& .haserror_boolean.checked": {
        "& .input_control label": {
          "& .label": { color: theme.palette.error.main },
          "& span svg": { color: theme.palette.error.main },
        },
      },
      "& .validation_note, & .validation_error": {
        width: "400px",
        "& .input_control label": { width: "auto" },
      },
    },
  },
}));

const displayWarningsErrors = (validations: IValidationError[]): JSX.Element => {
  if (validations?.length < 1) {
    return null;
  }
  const errorColor = validations[0].severity === ValidationErrorSeverity.error ? "error" : "secondary";
  return (
    <Grid container direction="column" style={{ marginBottom: 10 }}>
      <Grid container style={{ marginBottom: 4 }}>
        <WarningIcon color={errorColor} fontSize="small" />
        <Typography variant="body2" color={errorColor} style={{ fontSize: 16, fontWeight: 700, margin: "0px 4px" }}>
          {errorColor === "error" ? "Errors" : "Warnings"}
        </Typography>
        <Typography variant="caption" color="textPrimary" style={{ fontSize: 12, fontWeight: 300 }}>
          {errorColor === "error" ? "(Cannot submit with errors)" : "(Submitting now will include these warnings)"}
        </Typography>
      </Grid>
      {validations?.map((valErrors, index) => (
        <Typography key={`error_${index}`} variant="body2" color={errorColor}>
          {`${index + 1}) ${valErrors.title} - ${valErrors.description}`}
        </Typography>
      ))}
    </Grid>
  );
};

export const defaultFlightValues: Partial<IFullFlight> = {
  flightNumber: undefined,
  origin: undefined,
  destination: undefined,
  actualOut: undefined,
  actualOff: undefined,
  actualOn: undefined,
  actualIn: undefined,
  takeOff: undefined,
  landing: undefined,
};

export const getInitialFullFlights = async (flightIds: string[]): Promise<IFullFlight[]> => {
  const promises: Promise<IFullFlight>[] = [];
  flightIds?.forEach((flightId) => promises.push(getFlightById(flightId)));
  const flights = await Promise.all(promises);
  const flightsWithDefaults = flights?.map((fullFlights) => ({
    ...defaultFlightValues,
    ...fullFlights,
  }));
  return flightsWithDefaults;
};

const returnOnlyFlightChanges = (newFlights: IFullFlight[], initialflights: IFullFlight[]): Partial<IFullFlight>[] => {
  const flightsFieldDiff = newFlights?.reduce((acc, flight, index) => {
    const oldFlight = initialflights[index];
    const diff = getFormFieldDiff(flight, oldFlight);
    if (Object.keys(diff).length) {
      acc.push({ ...diff, _id: flight._id });
    }
    return acc;
  }, []);
  return flightsFieldDiff;
};

const formatLogsheetFormData = (initialValues: IContent): IContent => {
  const lastKnowValidation =
    initialValues?.validationHistory?.length > 0
      ? initialValues?.validationHistory?.sort(
          (a, b) => new Date(b.validatedAt).valueOf() - new Date(a.validatedAt).valueOf()
        )[0]
      : undefined;
  return {
    ...initialValues,
    validation: { isValid: lastKnowValidation?.isValid, hasError: lastKnowValidation?.hasError },
  };
};

export const ReviewContentWindow = (props: IReviewContentWindowProps): JSX.Element => {
  const { id } = props;
  const partialContent = props?.windowProps?.content;
  const dispatch = useDispatch();
  const theme: Theme = useTheme();
  const [content, setContent] = useState<IContent>(null);
  const [initialFormValues, setInitialFormValues] = useState<IContent>(null);
  const [logsheetFlightChanges, setLogsheetFlightchanges] = useState<Partial<IFullFlight>[]>([]);
  const [image, setImage] = useState<string>(null);
  const [imageError, setImageError] = useState<boolean>(false);
  const [validationResponse, setValidationResponse] = useState<IValidationError[]>([]);
  const [validateCustom, setValidateCustom] = useState<ValidateFunction>(null);
  const airlinePrimaryBase = useSelector(
    (state: IRootState) => state.structures.customerStructure.globalAppConfig.customer.customerPrimaryBase
  );
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);

  const validationErrors = useMemo(() => {
    return validationResponse?.filter((response) => response.severity === ValidationErrorSeverity.error);
  }, [validationResponse]);

  const validationWarnings = useMemo(() => {
    return validationResponse?.filter((response) => response.severity !== ValidationErrorSeverity.error);
  }, [validationResponse]);

  // The Lightbox displays the attachment in an `<img>`, so a PDF logsheet is rasterised to a
  // PNG first. Logsheets that arrive by email are already converted server-side and stored as
  // `.png`; ones uploaded through the UI keep their original `.pdf`, and this is what lets the
  // validator show both. Only page one is rendered, matching the email path.
  useEffect(() => {
    if (image || imageError) return undefined;
    let active = true;

    const loadImage = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.Get<any>(`/fileURL/${partialContent._id}/${partialContent.fileType}`);
      // No URL means no stored file: leave `image` unset so the "missing" message shows.
      if (!active || !response?.url) return;

      if (!isPdfFileType(partialContent.fileType)) {
        setImage(response.url);
        return;
      }

      try {
        const pdfResponse = await fetch(response.url);
        if (!pdfResponse.ok) throw new Error(`Failed to fetch the PDF logsheet: HTTP ${pdfResponse.status}`);
        const pngDataUrl = await renderPdfFirstPageToDataUrl(await pdfResponse.arrayBuffer());
        if (active) setImage(pngDataUrl);
      } catch (err) {
        console.error("Failed to render the PDF logsheet", err);
        if (active) setImageError(true);
      }
    };

    void loadImage();
    return () => {
      active = false;
    };
  }, [partialContent, image, imageError]);

  useEffect(() => {
    if (partialContent?._id && !content) {
      const getInitial = async (): Promise<void> => {
        setIsLoading(true);
        const response: IContent = await getInitialValues(partialContent._id, undefined, dispatch);
        if (response?._id) {
          setContent(response);
        }
        setIsLoading(false);
      };
      getInitial();
    }
  }, [partialContent, content, dispatch]);

  const getFullFlights = useCallback(async (data: IContent): Promise<void> => {
    setIsLoading(true);
    const updatedContent = { ...data };
    const flights = data?.associatedFlights;
    const flightIds = flights?.map((item) => (item as IFullFlight)?._id ?? item);
    const associatedFlights = await getInitialFullFlights(flightIds);
    setInitialFormValues({ ...updatedContent, associatedFlights });
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (content?.associatedFlights?.length > 0) {
      getFullFlights(content);
    } else {
      setInitialFormValues(content);
    }
  }, [content, getFullFlights]);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tabValue, setTabValue] = useState<string>("flightAssociations");
  const heightForForm = useMemo(() => CustomerFormHeight(), []);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const aircraft = useSelector((state: IRootState) => state.structures.customerStructure.aircraft);
  const userAttr = useSelector((state: IRootState) => state.authentication.attributes);
  const [searchAircraft, setSearchAircraft] = useState<IAircraft>(null);
  const [searchDepartureDate, setSearchDepartureDate] = useState<number>(null);
  const [firstFlightWithCrew, setFirstFlightWithCrew] = useState<IFlightWithCrew>(null);

  useEffect(() => {
    if (!searchDepartureDate && firstFlightWithCrew?.actualOff) {
      setSearchDepartureDate(firstFlightWithCrew?.actualOff?.valueOf());
    }
    if (!searchAircraft && firstFlightWithCrew?.aircraft?._id) {
      const aircraftSelection = aircraft.find((AC) => AC._id === firstFlightWithCrew?.aircraft?._id);
      setSearchAircraft(aircraftSelection);
    }
  }, [aircraft, firstFlightWithCrew, searchAircraft, searchDepartureDate]);

  const [availableFlights, setAvailableFlights] = useState<IFullFlight[]>([]);
  const [isSearchingFlights, setIsSearchingFlights] = useState<boolean>(false);
  const aircraftOptions: IAircraft[] = useMemo(() => Object.values(aircraft), [aircraft]);

  const debouncedSearchFlights = debounce(async (): Promise<void> => {
    setIsSearchingFlights(true);
    const searchDate = new Date(searchDepartureDate);
    const startOfDay = moment.utc(searchDate).startOf("day")?.valueOf();
    const endOfDay = moment.utc(searchDate).endOf("day")?.valueOf();

    const query: IDynamicQuery = {
      queryArgs: [
        { field: "aircraft._id", condition: "EQ", value: [searchAircraft?._id] },
        { field: "scheduledOut", condition: "BT", value: [startOfDay, endOfDay] },
      ],
      returnFields: ["custom"],
    };
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Searching available flights...",
      successMessage: "Available flights successfully found.",
      errorMessage: "Error: Unable to find available flights.",
    };
    const searchResults = await AmplifyAPIWrapper.Post<IContentAssociatedFlight>(
      "/searchFlights?excludeRemovedFlights=true", //don't search the allflights collection because I don't want the removed flights in the result
      query,
      notifications
    );
    setIsSearchingFlights(false);
    if (searchResults && Array.isArray(searchResults)) {
      const sortedResults = searchResults.sort((a: any, b: any) => (a.actualOut < b.actualOut ? -1 : 0));
      setAvailableFlights(sortedResults);
    }
  }, 1000);

  const handleSelectTab = (event: any, newValue: string): void => {
    event.preventDefault();
    event.stopPropagation();
    setTabValue(newValue);
  };

  const mosaicPanelName = useMemo(() => `${id}-panel`, [id]);
  const mosaicPane = useMemo(() => {
    return id ? document.getElementById(mosaicPanelName) : null;
  }, [id, mosaicPanelName]);

  const returnFlightDiff = useCallback(
    (newFlights: IFullFlight[]): Partial<IFullFlight>[] => {
      const oldFlights = initialFormValues?.associatedFlights as IFullFlight[];
      return returnOnlyFlightChanges(newFlights, oldFlights);
    },
    [initialFormValues]
  );

  const handleSubmitReview = useCallback(
    async (values: IContent, helpers: FormikHelpers<IContent>): Promise<void> => {
      const { setSubmitting, setStatus, resetForm } = helpers;
      setSubmitting(true);
      const associatedFlightIds = (values?.associatedFlights as IFullFlight[])?.map((flight) => flight._id);
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: "Submitting Review of Logsheet...",
        successMessage: "Review Submitted",
        errorMessage: "Error: Unable to Submit Review",
      };
      const submittedBy = `${userAttr.firstName} ${userAttr.lastName} <${userAttr.email}>`;
      const newFlights = values?.associatedFlights as IFullFlight[];
      const localTZ = airportOptions[airlinePrimaryBase].timezoneId;
      const logSheetDate = moment.utc(new Date(firstFlightWithCrew?.actualOff)).tz(localTZ).startOf("day").valueOf();
      const submitDate = moment.utc(Date.now()).tz(localTZ).valueOf();
      const payload: ILogsheetPayload = {
        content: {
          ...values,
          isValid: values?.validation?.isValid ?? false,
          associatedFlights: associatedFlightIds,
          fields: {
            ...values.fields,
            logSheetDate: logSheetDate,
            aircraft: firstFlightWithCrew?.aircraft?.registration ?? values.fields.aircraft,
            errors: values?.validation?.errorReason ?? "",
            submittedBy: submittedBy,
            dateSubmitted: submitDate,
          },
          validation: { ...values?.validation, isValid: values?.validation?.isValid ?? false },
        },
        flights: returnFlightDiff(newFlights),
        override: validationResponse,
      };

      const response: any = await AmplifyAPIWrapper.Put<ILogsheetPayload>(
        `/logsheet/${values?._id}`,
        payload,
        notifications
      );

      const submissionErrors = response?.errors?.length > 0 ? response?.errors : [];
      setValidationResponse(submissionErrors);

      if (response?.content?._id && !response?.errors) {
        const newFormValues = {
          ...response?.content,
          associatedFlights: values.associatedFlights,
        };
        setInitialFormValues(newFormValues);
        setStatus({ success: true });
      } else {
        setStatus({ success: false });
      }

      setSubmitting(false);
    },
    [dispatch, validationResponse, returnFlightDiff, firstFlightWithCrew, airportOptions, airlinePrimaryBase, userAttr]
  );

  const validateFlightTimes = (flights: IFullFlight[]): any => {
    const flightErrors: { [key: string]: any } = {};
    flights.forEach((flight, index) => {
      const previousFlightIn = flights[index - 1]?.actualIn;
      const nextFlightOut = flights[index + 1]?.actualOut;

      // OUT after OFF/ON/IN. (or before previous flight/ after next flight)
      if (
        flight?.actualOut > flight?.actualOff ||
        flight?.actualOut > flight?.actualOn ||
        flight?.actualOut > flight?.actualIn ||
        flight?.actualOut < previousFlightIn ||
        flight?.actualOut > nextFlightOut
      ) {
        flightErrors[index] = { ...flightErrors[index], actualOut: "Out of bounds" };
      }

      // Took OFF before OUT or after ON/IN (or before previous flight/ after next flight)
      if (
        flight?.actualOff < flight?.actualOut ||
        flight?.actualOff > flight?.actualOn ||
        flight?.actualOff > flight?.actualIn ||
        flight?.actualOff < previousFlightIn ||
        flight?.actualOff > nextFlightOut
      ) {
        flightErrors[index] = { ...flightErrors[index], actualOff: "Out of bounds" };
      }

      // ON BEFORE OUT/OFF or after IN. (or before previous flight/ after next flight)
      if (
        flight?.actualOn < flight?.actualOut ||
        flight?.actualOn < flight?.actualOff ||
        flight?.actualOn > flight?.actualIn ||
        flight?.actualOn < previousFlightIn ||
        flight?.actualOn > nextFlightOut
      ) {
        flightErrors[index] = { ...flightErrors[index], actualOn: "Out of bounds" };
      }

      // IN BEFORE OUT/OFF/ON (or before previous flight/ after next flight)
      if (
        flight?.actualIn < flight?.actualOut ||
        flight?.actualIn < flight?.actualOff ||
        flight?.actualIn < flight?.actualOn ||
        flight?.actualIn < previousFlightIn ||
        flight?.actualIn > nextFlightOut
      ) {
        flightErrors[index] = { ...flightErrors[index], actualIn: "Out of bounds" };
      }
    });
    return flightErrors;
  };

  const validateCustomFields = useCallback(
    (formValues: IContent, validateCustom: ValidateFunction): any => {
      if (!formValues) {
        return {};
      }
      const { associatedFlights, validation } = formValues;
      const formErrors: any = {};

      if (!validation?.isValid && !validation?.hasError) {
        formErrors.validation = { isValid: "Logsheet has to be marked valid or with errors." };
      } else if (validation?.hasError && !validation?.errorReason) {
        formErrors.validation = { errorReason: "Error Info required." };
      }

      if (validateCustom) {
        associatedFlights?.forEach((flight, index) => {
          const value = (flight as IFullFlight)?.custom ?? {};
          const isValid = validateCustom(value);
          const { schema, errors } = validateCustom;

          const properties = Object.values((schema as any).properties);
          properties.forEach((prop: any) => {
            const key = prop?.$id.split("/").pop();
            if (!isValid) {
              const errorItem = errors?.find(
                (item) => item.dataPath === `.${key}` || item.dataPath === `.${key}.value`
              );
              const error =
                errorItem && errorItem?.keyword === "required"
                  ? "required"
                  : errorItem && errorItem?.keyword !== "required"
                    ? errorItem?.message
                    : undefined;

              const currentCustomErrors = formErrors?.associatedFlights?.[index]?.custom;

              if (errorItem) {
                formErrors.associatedFlights = {
                  ...formErrors.associatedFlights,
                  [index]: { ...flightErrors[index], custom: { ...currentCustomErrors, [key]: error } },
                };
              }
            }
          });
        });
      }

      const flightErrors = validateFlightTimes(formValues?.associatedFlights as IFullFlight[]);
      if (Object.entries(flightErrors)?.length > 0) {
        formErrors.associatedFlights = flightErrors;
      }

      const flightChanges = returnFlightDiff(formValues?.associatedFlights as IFullFlight[]);
      setLogsheetFlightchanges(flightChanges);

      if (!formValues?.fields?.logSheetNumber) {
        formErrors.fields = { logSheetNumber: "required" };
      }
      return formErrors;
    },
    [returnFlightDiff]
  );

  if (initialFormValues === null) {
    return null;
  }

  return (
    <OCReviewContentWindow ref={containerRef}>
      {isLoading ? (
        <LoadingWrapper message="loading flight log" />
      ) : (
        <Formik
          className={"flightlog_form"}
          initialValues={formatLogsheetFormData(initialFormValues)}
          validate={(value: any) => validateCustomFields(value, validateCustom)}
          enableReinitialize={true}
          onSubmit={handleSubmitReview}
        >
          {(contentForm: IContentForm) => {
            const { errors, values, dirty, isValid, isSubmitting, handleReset } = contentForm;
            const containsErrors =
              errors && !!Object.keys(errors)?.length && !(Object.keys(errors)?.length === 1 && !!errors.validation);
            const hasFlights = values?.associatedFlights?.length > 0;
            const hasCrew = firstFlightWithCrew?.crew?.length > 0;
            const toggleAssociatedFlight = (flight: IFullFlight): void => {
              const { values, setFieldValue, setFieldTouched } = contentForm;
              if (flight && values && setFieldValue && setFieldTouched) {
                const newAssociatedFlights = [...values.associatedFlights];
                const removeIndex = (newAssociatedFlights as IFullFlight[]).findIndex((f) => f._id === flight?._id);
                if (removeIndex > -1) {
                  newAssociatedFlights.splice(removeIndex, 1);
                } else {
                  newAssociatedFlights.push(flight);
                  newAssociatedFlights.sort((a: any, b: any) => (a.actualOut < b.actualOut ? -1 : 0));
                }
                const updateFormValues = {
                  ...contentForm?.values,
                  associatedFlights: newAssociatedFlights as IFullFlight[],
                };
                setInitialFormValues(updateFormValues);
              }
            };

            return (
              <Form style={{ height: "100%", width: "100%" }}>
                <Grid className="content_pane">
                  <Grid
                    container
                    item
                    xs={12}
                    className={"lightbox"}
                    sx={{
                      height: `calc(100% - ${heightForForm}px + 70px)`,
                      minHeight: "200px",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    {image && content && !imageError ? (
                      <Lightbox
                        image={image}
                        title={`Logsheet Image: ${content?.fileName ?? ""}`}
                        zoomStep={0.2}
                        defaultValues={content?.lightBoxState ?? null}
                        onChange={(state: ILightboxState) => {
                          contentForm.setFieldValue("lightBoxState", state);
                          contentForm.setFieldTouched("lightBoxState", true);
                        }}
                        onError={() => setImageError(true)}
                        keyboardInteraction={false}
                      />
                    ) : (
                      <Grid
                        container
                        alignItems="center"
                        justifyContent="center"
                        style={{
                          width: "100%",
                          minHeight: 300,
                          height: tabValue === "fieldEntries" ? `calc(100% - ${heightForForm + 20}px)` : "100%",
                          backgroundColor: "#000",
                        }}
                      >
                        <Alert severity="error" variant="outlined" style={{ width: 400 }}>
                          <AlertTitle style={{ color: "#FFF" }}>
                            {imageError ? "Logsheet Could Not Be Displayed!" : "Logsheet Image Missing!"}
                          </AlertTitle>
                          {imageError && `This ${content?.fileType ?? "file"} could not be displayed. `}
                          Please see administrator for assistance.
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                  <Grid
                    container
                    style={{ width: "100%", marginTop: "10px", height: `${heightForForm + 20}px` }}
                    direction="row"
                  >
                    <Grid item xs={12} sx={{ display: "flex", justifyContent: "start" }}>
                      <Typography variant="subtitle2" style={{ fontSize: 16, marginRight: 10 }}>
                        Logsheet Review Steps
                      </Typography>
                      <OcTabs
                        variant="scrollable"
                        orientation="horizontal"
                        scrollButtons="auto"
                        value={tabValue}
                        onChange={handleSelectTab}
                      >
                        <Tab
                          data-testid="associate-flights-tab"
                          wrapped={true}
                          value="flightAssociations"
                          label="1) Associate Flights"
                          classes={{ root: "form-tab" }}
                          style={{ color: !hasFlights || !hasCrew ? "red" : "inherit" }}
                        />
                        <Tab
                          data-testid="review-logsheet-tab"
                          wrapped={true}
                          value="fieldEntries"
                          label="2) Review Logsheet"
                          classes={{ root: "form-tab" }}
                          style={{ color: dirty ? theme?.custom?.forms?.dirtyColor : "inherit" }}
                          disabled={!hasFlights}
                        />
                        <Tab
                          data-testid="submit-validation-tab"
                          wrapped={true}
                          value="finalReview"
                          label="3) Submit Validation"
                          classes={{ root: "form-tab" }}
                          disabled={!hasFlights || containsErrors}
                        />
                      </OcTabs>
                      <Grid container className="form_actions">
                        <Button
                          className="btn refresh"
                          variant="contained"
                          color="primary"
                          onClick={(event) => {
                            getFullFlights(values);
                            setValidationResponse([]);
                          }}
                        >
                          Refresh Flights
                        </Button>
                        <Button
                          className="btn reset"
                          type="reset"
                          variant="contained"
                          color="secondary"
                          disabled={(!dirty && isValid) || isSubmitting}
                          onClick={(event) => {
                            handleReset(event);
                            setLogsheetFlightchanges([]);
                            setValidationResponse([]);
                          }}
                        >
                          Reset
                        </Button>
                        <Button
                          className={"btn save"}
                          data-testid="submit-review-button"
                          variant="contained"
                          color="primary"
                          disabled={
                            isSubmitting ||
                            !isValid ||
                            !dirty ||
                            validationErrors?.length > 0 ||
                            tabValue !== "finalReview" ||
                            containsErrors
                          }
                          onClick={debounce(() => handleSubmitReview(values, contentForm), 500)}
                        >
                          Submit Review
                        </Button>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} sx={{ minHeight: `${heightForForm + 10}px`, marginTop: "10px" }}>
                      <Grid
                        container
                        wrap="nowrap"
                        justifyContent="space-between"
                        style={{ display: tabValue === "flightAssociations" ? "flex" : "none", overflow: "auto" }}
                      >
                        <Grid item className="available_flights">
                          <Grid container className="search_flight_fields" direction="row">
                            <SelectField
                              name="search_aircraft"
                              data-testid="search-aircraft-select"
                              className="search_aircraft"
                              isMulti={false}
                              blurInputOnSelect={true}
                              closeMenuOnSelect={true}
                              valueKey="_id"
                              value={searchAircraft}
                              onChange={setSearchAircraft}
                              options={aircraftOptions}
                              getOptionLabel={(aircraft: IAircraft) =>
                                aircraft
                                  ? `${aircraft?.registration} ${
                                      aircraft?.aircraftType?.title && "(" + aircraft.aircraftType.title + ")"
                                    }`
                                  : ""
                              }
                              getOptionValue={(aircraft: IAircraft) => aircraft._id}
                              menuPortalTarget={mosaicPane}
                              menuPosition="fixed"
                              isSearchable={true}
                              noOptionsMessage={() => "Aircraft"}
                              openMenuOnFocus={false}
                              label="Aircraft"
                              placeholder="Select Aircraft"
                            />
                            <DateTimeField
                              name="search_departure"
                              data-testid="select-departure-date"
                              label="Departure Date"
                              className="search_departure"
                              value={searchDepartureDate}
                              onChange={(value) => setSearchDepartureDate(value as number)}
                              isEpoch={true}
                              hideTimeControl={true}
                            />
                            <Button
                              id={"search_flights_btn"}
                              data-testid="search-flights-button"
                              className="search_flights_btn"
                              variant="outlined"
                              onClick={() => debouncedSearchFlights()}
                              disabled={isSearchingFlights || !searchAircraft || !searchDepartureDate}
                            >
                              {!isSearchingFlights ? (
                                <>
                                  <Icon className="search_icon">
                                    {availableFlights?.length > 0 ? "refresh" : "search"}
                                  </Icon>
                                  {availableFlights?.length > 0 ? "Refresh" : "Search"}
                                </>
                              ) : (
                                <>
                                  <CircularProgress color="inherit" style={{ width: 14, height: 14, marginRight: 8 }} />{" "}
                                  searching
                                </>
                              )}
                            </Button>
                          </Grid>
                          <Grid
                            container
                            className="available_flights_container"
                            data-testid="available-flights-container"
                          >
                            {availableFlights?.length > 0 ? (
                              availableFlights.map((flight, index) => {
                                const isSelected = !!(values?.associatedFlights as IFullFlight[])?.find(
                                  (ass) => ass._id === flight._id
                                );
                                return (
                                  <Grid container wrap="nowrap" alignItems="center" key={index}>
                                    <CheckboxField value={isSelected} onChange={() => toggleAssociatedFlight(flight)} />
                                    <RenderFlightInfo
                                      key={index}
                                      flight={flight}
                                      searchDepartureDate={searchDepartureDate}
                                      type="available_flight"
                                    />
                                  </Grid>
                                );
                              })
                            ) : (
                              <Typography variant="caption" color="textPrimary" style={{ marginLeft: 15 }}>
                                To find available flights, use the search above. Once found, select the flights using
                                the checkboxes or drag them into the logsheet flights window.
                              </Typography>
                            )}
                          </Grid>
                        </Grid>

                        <Grid item className="associated_flights">
                          {!hasFlights && (
                            <Grid item style={{ margin: "0px 10px" }}>
                              <Typography variant="caption" color="error">
                                Logsheet has no associated flights! Add flights listed in the logsheet above to continue
                                to review tab.
                              </Typography>
                            </Grid>
                          )}
                          {hasFlights && !hasCrew && (
                            <Grid item style={{ margin: "0px 10px" }}>
                              <Typography variant="caption" color="error">
                                Flights are missing crew information, update/add to pairing. Note: Crew info will be
                                missing from the logsheet.
                              </Typography>
                            </Grid>
                          )}

                          <Typography variant="body2" color="textPrimary" style={{ margin: 5 }}>
                            Logsheet Flights
                          </Typography>
                          <AssociatedFlightDropTarget
                            formProps={contentForm}
                            searchDepartureDate={searchDepartureDate}
                            setFirstFlightWithCrew={setFirstFlightWithCrew}
                          />
                        </Grid>
                      </Grid>

                      <Grid
                        container
                        direction="column"
                        style={{
                          display: tabValue === "fieldEntries" ? "flex" : "none",
                          width: "auto",
                          height: "100%",
                          position: "relative",
                        }}
                      >
                        {/* Always render error message with reserved space to prevent layout shift when errors appear/disappear */}
                        <Typography
                          variant="caption"
                          color="error"
                          style={{
                            position: "absolute",
                          }}
                        >
                          {containsErrors
                            ? "Logsheet contains errors or missing info preventing it from being submitted."
                            : ""}
                        </Typography>

                        <CustomerLogsheet
                          id={id}
                          formProps={contentForm}
                          firstFlightWithCrew={firstFlightWithCrew}
                          setValidateCustom={setValidateCustom}
                        />
                      </Grid>

                      <Grid
                        container
                        direction="column"
                        className="final_review"
                        style={{ display: tabValue === "finalReview" ? "flex" : "none" }}
                      >
                        {logsheetFlightChanges?.length > 0 && (
                          <Grid container className="pending_flight_updates" alignItems="center">
                            <Typography
                              className="cell_header"
                              variant="body2"
                              color="textPrimary"
                              style={{ width: "auto", margin: "0px 5px" }}
                            >
                              Review submission will update the following flights
                            </Typography>

                            {logsheetFlightChanges?.map((pendingChange, index) => {
                              const pendingFlight = (contentForm?.values?.associatedFlights as IFullFlight[]).find(
                                (flight) => flight._id === pendingChange?._id
                              );
                              return pendingFlight ? (
                                <Grid
                                  container
                                  key={`pending_update_${index}`}
                                  style={{ width: "auto", margin: "0px 5px" }}
                                >
                                  <FlightChip data={pendingFlight} disableContext={false} disableFocus={false} />
                                </Grid>
                              ) : null;
                            })}
                          </Grid>
                        )}

                        {displayWarningsErrors(validationErrors)}
                        {displayWarningsErrors(validationWarnings)}

                        <Grid container>
                          <Grid item className="validation_container" style={{ marginRight: 10, marginBottom: 10 }}>
                            <Typography className="cell_header" variant="body2" color="textPrimary">
                              Validation
                            </Typography>
                            <Grid container direction="column">
                              <Grid container>
                                <FormikCheckboxField
                                  data-testid="validation-is-valid"
                                  className="field checkboxfield isvalid_boolean"
                                  label="Is Valid"
                                  name="validation.isValid"
                                />
                                <FormikCheckboxField
                                  className="field checkboxfield haserror_boolean"
                                  label="Has Errors"
                                  name={"validation.hasError"}
                                />
                              </Grid>
                              <Grid item>
                                <FormikTextField
                                  className="field textfield validation_note"
                                  label="Notes"
                                  name="validation.note"
                                  multiline={true}
                                  minRows="2"
                                  maxRows="5"
                                />
                              </Grid>
                              <Grid item>
                                <FormikTextField
                                  className="field textfield validation_error"
                                  label="Errors"
                                  name="validation.errorReason"
                                  multiline={true}
                                  minRows="2"
                                  maxRows="5"
                                />
                              </Grid>
                            </Grid>
                          </Grid>

                          {values?.validationHistory?.length > 0 && (
                            <Grid item className="validation_history_container">
                              <Grid container direction="column" key={"validation_history_entries"}>
                                <Typography className="cell_header" variant="body2" color="textPrimary">
                                  Validation History
                                </Typography>
                                {values?.validationHistory
                                  ?.sort((a, b) => (a.validatedAt < b.validatedAt ? 1 : -1))
                                  .map((validationEntry: IValidation, index) => {
                                    return (
                                      <Grid
                                        container
                                        direction="column"
                                        style={{ marginBottom: 30 }}
                                        key={`validation_history_${index}`}
                                      >
                                        <Grid container direction="column">
                                          <Grid container alignItems="center">
                                            <CheckboxField
                                              name={`validationHistory.${index}.isValid`}
                                              className="field checkboxfield isvalid_boolean"
                                              label="Is Valid"
                                              value={validationEntry.isValid}
                                              disabled={true}
                                            />
                                            <CheckboxField
                                              name={`validationHistory.${index}.hasError`}
                                              className="field checkboxfield haserror_boolean"
                                              label="Has Errors"
                                              value={validationEntry.hasError}
                                              disabled={true}
                                            />
                                          </Grid>
                                          <Grid item>
                                            <TextField
                                              name={`validationHistory.${index}.note`}
                                              className="field textfield validation_note"
                                              label="Notes"
                                              value={validationEntry.note}
                                              multiline={true}
                                              minRows="2"
                                              maxRows="5"
                                              disabled={true}
                                            />
                                          </Grid>
                                          <Grid item>
                                            <TextField
                                              name={`validationHistory.${index}.errorReason`}
                                              className="field textfield validation_error"
                                              label="Errors"
                                              value={validationEntry.errorReason}
                                              multiline={true}
                                              minRows="2"
                                              maxRows="5"
                                              disabled={true}
                                            />
                                          </Grid>

                                          {validationEntry?.validatedErrors?.length > 0 && (
                                            <Grid container direction="column" style={{ marginBottom: 10 }}>
                                              <Grid container style={{ marginBottom: 4 }}>
                                                <WarningIcon color="secondary" fontSize="small" />
                                                <Typography
                                                  variant="body2"
                                                  color="secondary"
                                                  style={{ fontSize: 16, fontWeight: 700, margin: "0px 4px" }}
                                                >
                                                  {"Errors / Warnings"}
                                                </Typography>
                                              </Grid>
                                              {validationEntry?.validatedErrors?.map((valErrors, valIndex) => (
                                                <Typography
                                                  key={`entry_${index}_error_${valIndex}`}
                                                  variant="body2"
                                                  color={
                                                    valErrors.severity === ValidationErrorSeverity.error
                                                      ? "error"
                                                      : "secondary"
                                                  }
                                                >
                                                  {`${index + 1}) ${valErrors.title} - ${valErrors.description}`}
                                                </Typography>
                                              ))}
                                            </Grid>
                                          )}

                                          <Grid item style={{ fontSize: 12, padding: "0px 5px" }}>
                                            {`Submitted on ${formatDateString(
                                              validationEntry.validatedAt,
                                              "Etc/UTC"
                                            )} by `}
                                            <ShowUserInfo
                                              userId={validationEntry.validatedBy}
                                              getUserLabel={(user: IUser) => `${user.firstName} ${user.lastName}`}
                                            />
                                          </Grid>
                                        </Grid>
                                      </Grid>
                                    );
                                  })}
                              </Grid>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      )}
    </OCReviewContentWindow>
  );
};
